import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { ReportSingleService } from '@services/report-single.service';
import { KebabMenuComponent } from '@shared/kebab-menu/kebab-menu.component';
import { ReportSchedulerStatusIndicatorComponent } from '@shared/report-scheduler-status-indicator/report-scheduler-status-indicator.component';
import { SectionHeaderComponent } from '@shared/section-header/section-header.component';
import { DropdownPanelComponent, IconSanitizerService, TooltipDirective } from 'ngx-lib';
import { IReportHeaderConfig } from './report-single-header.config';

@Component({
    selector: 'app-report-single-header',
    standalone: true,
    imports: [
        SectionHeaderComponent,
        TooltipDirective,
        DropdownPanelComponent,
        ReportSchedulerStatusIndicatorComponent,
        KebabMenuComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './report-single-header.component.html'
})
export class ReportSingleHeaderComponent {
    private readonly service = inject(ReportSingleService);
    private readonly iconSanitizer = inject(IconSanitizerService);

    readonly headerConfig = computed<IReportHeaderConfig>(() => ({
        reportData: this.service.reportData(),
        reportSubtitle: this.service.reportSubtitle(),
        hasRunTemplateReport: this.service.hasRunTemplateReport(),
        canUpdateReport: this.service.canUpdateReport(),
        canDeleteReport: this.service.canDeleteReport(),
        canScheduleReport: this.service.canScheduleReport(),
        reportId: this.service.reportId(),
        catalogReportId: this.service.catalogReportId(),
        downloadPanel: this.service.downloadPanel
    }));

    readonly saveIcon?: SafeHtml;
    readonly shareIcon?: SafeHtml;
    readonly downloadIcon?: SafeHtml;
    readonly scheduleIcon?: SafeHtml;

    readonly showSchedulerStatus = computed(
        () =>
            !!(
                this.headerConfig().reportData?.schedulerId &&
                this.headerConfig().reportData?.schedulerStatusId &&
                this.headerConfig().canScheduleReport
            )
    );
    readonly showDownload = computed(
        () => this.headerConfig().hasRunTemplateReport && !!this.headerConfig().downloadPanel
    );
    readonly showShare = computed(() => this.headerConfig().hasRunTemplateReport);
    readonly showKebabMenu = computed(
        () =>
            this.headerConfig().canUpdateReport ||
            (!!this.headerConfig().reportId && !this.headerConfig().catalogReportId) ||
            this.headerConfig().canDeleteReport
    );
    readonly showFavoriteOption = computed(
        () => !!this.headerConfig().reportId && !this.headerConfig().catalogReportId
    );

    constructor() {
        this.saveIcon = this.iconSanitizer.getIcon('save');
        this.shareIcon = this.iconSanitizer.getIcon('share');
        this.downloadIcon = this.iconSanitizer.getIcon('download');
        this.scheduleIcon = this.iconSanitizer.getIcon('schedule');
    }

    saveAsCustom(): void {
        void this.service.saveAsCustom();
    }

    save(): void {
        void this.service.save();
    }

    share(): void {
        void this.service.share();
    }

    schedule(): void {
        void this.service.schedule();
    }

    edit(): void {
        void this.service.edit();
    }

    toggleFavorite(): void {
        const id = this.headerConfig().reportId;
        if (id == null) return;
        void this.service.toggleFavorite(id);
    }

    deleteReport(): void {
        void this.service.deleteReport();
    }
}
